import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { Command } from "cmdk";
import { CornerDownLeft, Search } from "lucide-react";
import { cn } from "../../lib/cn";
import { NAV_ITEMS } from "../../layouts/AppShell";

/**
 * Cmd+K / Ctrl+K navigation.
 *
 * Navigation only, by design — no data search. Searching contacts or messages from here would
 * need query endpoints and debounced fetches; jumping between pages needs neither, so this stays
 * entirely frontend and instant.
 *
 * Built on cmdk (already a dependency, it powers the timezone combobox) wrapped in the same Radix
 * Dialog the app's Modal uses, so the overlay, blur, enter/exit animation and focus trap are the
 * ones already established rather than a second overlay system.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // metaKey covers macOS, ctrlKey covers Windows/Linux.
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      // Ctrl+K is the browser's "focus address bar" on some platforms, and the app's own
      // shortcut should win while the app has focus.
      event.preventDefault();
      setOpen((o) => !o);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  function go(to: string) {
    setOpen(false);
    navigate(to);
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]",
            "data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out",
          )}
        />
        <Dialog.Content
          className={cn(
            // Sits high rather than centred: the eye is already at the top when reaching for a
            // jump-to shortcut, and it leaves the page visible behind for context.
            "fixed left-1/2 top-[20vh] z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2",
            "overflow-hidden rounded-card border border-border bg-surface shadow-lg",
            "focus:outline-none",
            "data-[state=open]:animate-content-in data-[state=closed]:animate-content-out",
          )}
        >
          <Dialog.Title className="sr-only">Jump to a page</Dialog.Title>
          <Dialog.Description className="sr-only">
            Type to filter pages, then press Enter to navigate.
          </Dialog.Description>

          <Command loop>
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search className="size-4 shrink-0 text-text-muted" aria-hidden />
              <Command.Input
                autoFocus
                placeholder="Jump to a page…"
                className="h-12 w-full bg-transparent text-sm text-text outline-none placeholder:text-text-muted"
              />
            </div>

            <Command.List className="scrollbar-slim max-h-72 overflow-y-auto overscroll-contain p-1.5">
              <Command.Empty className="px-3 py-8 text-center text-sm text-text-muted">
                No page matches that.
              </Command.Empty>

              <Command.Group
                heading="Pages"
                className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-muted"
              >
                {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                  <Command.Item
                    key={to}
                    // The path is part of the searchable value, so typing "api" or "keys" both hit.
                    value={`${label} ${to}`}
                    onSelect={() => go(to)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2.5 rounded-control px-2.5 py-2 text-sm",
                      "text-text-muted",
                      "data-[selected=true]:bg-surface-hover data-[selected=true]:text-text",
                    )}
                  >
                    <Icon className="size-4 shrink-0" aria-hidden />
                    <span className="flex-1">{label}</span>
                    <CornerDownLeft
                      aria-hidden
                      className="size-3.5 opacity-0 data-[active=true]:opacity-100"
                    />
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>

            <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-text-muted">
              <span className="flex items-center gap-1.5">
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
                to navigate
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>Enter</Kbd>
                to open
                <span aria-hidden className="text-border">|</span>
                <Kbd>Esc</Kbd>
                to close
              </span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-border bg-bg px-1.5 py-0.5 font-sans text-[11px] leading-none text-text-muted">
      {children}
    </kbd>
  );
}
